import React, { useState, useRef, useEffect, forwardRef } from "react";
import { Popover, Paper, IconButton, Box, Typography, InputAdornment } from "@mui/material";
import TreeViewComponent from "@CubeComponents/html/TreeView";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import IconComponent from "@CubeComponents/icons";
import { useTranslation } from "@i18n";
import Text from "@CubeComponents/html/Text/Text";
const TreeSelectDropDown = forwardRef(
  (
    {
      treeData = [],
      placeholder = "Choose an option...",
      enableCheckboxSelection = false,
      onSelectionChange,
      value = "",
      disabled = false,
      onSelectedRowIdsChanged,
      disabledParentSelection = false,
      allowDeselection = true,
      popoverHeight = "28.125rem",
      popoverWidth = "18.75rem",
      selectCheckBoxOnRowClick,
      inputFieldHeight = "auto",
      ...rest
    }: any,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState(value);
    const textFieldRef = useRef<any>(null);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const innerRef = useRef<any>(null);
    const treeRef: any = ref || innerRef;
    const { t } = useTranslation();
    const lastSelectedIdRef = useRef<string | null>(null);
    const handleRowSelection = (e: any) => {
      const selectedRowIds: string[] = e.detail?.args?.selectedRowIds ?? [];
      const dv = treeRef.current?.dataView;
      if (!dv) return;

      // Prevent deselection when allowDeselection is false
      if (selectedRowIds.length === 0) {
        if (!allowDeselection && lastSelectedIdRef.current) {
          dv.setSelectedIds([lastSelectedIdRef.current]);
        }
        return;
      }

      const selectedId = selectedRowIds[0];
      const item = dv.getItemById(selectedId);
      if (!item) return;

      if (disabledParentSelection && item.__hasChildren) {
        e.stopPropagation();
        // Restore previous selection if any, otherwise clear
        dv.setSelectedIds(lastSelectedIdRef.current ? [lastSelectedIdRef.current] : []);
        return;
      }

      lastSelectedIdRef.current = selectedId ?? null;
      handleTreeSelection(item);

      if (onSelectedRowIdsChanged) {
        onSelectedRowIdsChanged(e);
      }
    };
    const handleTextFieldClick = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      setIsOpen(true);
      setAnchorEl(e.currentTarget);
    };
    const handleClose = (_event: unknown, reason?: string) => {
      // Only close on backdrop click or escape key — not on focus/DOM changes from tree expand/collapse
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        setIsOpen(false);
        setAnchorEl(null);
      }
    };
    const closeDropdown = () => {
      setIsOpen(false);
      setAnchorEl(null);
    };
    const handleTreeSelection = (params: any) => {
      setSelectedValues(params.label);
      closeDropdown();
      if (onSelectionChange) onSelectionChange(params);
    };
    useEffect(() => {
      setSelectedValues(value);
    }, [value]);
    return (
      <>
        <TextFieldComponent
          inputRef={textFieldRef}
          placeholder={placeholder}
          disabled={disabled}
          value={selectedValues}
          onClick={handleTextFieldClick}
          height={inputFieldHeight}
          sx={{
            "& .MuiInputBase-input": {
              cursor: "pointer",
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" size="small" disabled={disabled}>
                    <IconComponent
                      name="DropdownIcon"
                      width="1rem"
                      height="1rem"
                      className={`transition-transform duration-200 w-4 h-4 ${isOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                cursor: "pointer",
                "& input": {
                  cursor: "pointer",
                },
              },
            },
            htmlInput: {
              readOnly: true,
            },
          }}
        />
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: {
                width: popoverWidth,
                maxHeight: popoverHeight,
                mt: 1,
                boxShadow: "0rem 0.125rem 0.25rem rgba(0, 0, 0, 0.09)",
              },
            },
          }}
        >
          <Paper>
            <Box>
              {treeData.length > 0 ? (
                <TreeViewComponent
                  ref={treeRef}
                  treeData={treeData}
                  onCheckChange={handleTreeSelection}
                  enableCheckboxSelection={enableCheckboxSelection}
                  height={popoverHeight}
                  width="100%"
                  columnWidth={popoverWidth - 32}
                  enableFiltering
                  enableSearch
                  disableMarginForNoData
                  onSelectedRowIdsChanged={handleRowSelection}
                  selectCheckBoxOnRowClick={selectCheckBoxOnRowClick ?? true}
                  columnId="label"
                  identifierPropName="id"
                  parentPropName="parentId"
                  disableHeader={true}
                  disabledParentSelection
                  showPageCount={false}
                  {...rest}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: popoverHeight,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    <Text>{t("No items found")}</Text>
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Popover>
      </>
    );
  },
);
TreeSelectDropDown.displayName = "TreeSelectDropDown";
export default TreeSelectDropDown;
